
import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import PageTransition from '@/components/PageTransition';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn } = useAuth();
  
  // Check if there's a return path in the location state
  const returnTo = location.state?.returnTo || '/account';

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing again
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    if (!formData.email || !formData.password) {
      setError("Please enter your email and password");
      setLoading(false);
      return;
    }
    
    try {
      const { success, error } = await signIn(formData.email, formData.password);

      if (success) {
        toast.success("Login Successful", {
          description: "You have been logged in successfully."
        });
        // If there's an explicit returnTo, use it. Otherwise detect signup progress.
        if (location.state?.returnTo) {
          navigate(returnTo);
        } else {
          // Check if user has connected leagues and subscription
          try {
            const { data: leagues } = await supabase.from('user_leagues').select('league_id').limit(1);
            const hasLeagues = leagues && leagues.length > 0;
            if (!hasLeagues) {
              navigate('/league-selection');
            } else {
              const { data: subData } = await supabase.functions.invoke('get-subscription-status');
              if (!subData?.hasValidSubscription) {
                navigate('/pricing');
              } else {
                navigate('/account');
              }
            }
          } catch {
            navigate('/account');
          }
        }
      } else {
        const errorMessage = error?.message || "Invalid email or password. Please try again.";
        setError(errorMessage);
        toast.error("Login Failed", {
          description: errorMessage
        });
      }
    } catch (error: any) {
      const errorMessage = error?.message || "An unexpected error occurred.";
      setError(errorMessage);
      toast.error("Login Error", {
        description: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <PageTransition>
        <main className="flex-1 flex items-center justify-center py-20 px-6">
          <div className="w-full max-w-md">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold mb-2">Welcome Back</h1>
              <p className="text-muted-foreground">
                Log in to access your fantasy football insights
              </p>
            </div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="glass rounded-xl p-6 md:p-8"
            >
              {error && (
                <Alert variant="destructive" className="mb-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    autoComplete="email"
                    className={error ? "border-destructive" : ""}
                  />
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Password</Label>
                    <button
                      type="button"
                      className="text-xs text-primary hover:underline"
                      onClick={async () => {
                        if (!formData.email) {
                          toast.error("Enter your email first, then click Forgot password");
                          return;
                        }
                        try {
                          const { error } = await supabase.auth.resetPasswordForEmail(formData.email, {
                            redirectTo: `${window.location.origin}/reset-password`,
                          });
                          if (error) {
                            toast.error(error.message);
                          } else {
                            toast.success("Check your email for a password reset link");
                          }
                        } catch {
                          toast.error("Something went wrong. Try again.");
                        }
                      }}
                    >
                      Forgot password?
                    </button>
                  </div>
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    autoComplete="current-password"
                    className={error ? "border-destructive" : ""}
                  />
                </div>
                
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? "Logging In..." : "Log In"}
                </Button>
                
                <p className="text-sm text-center text-muted-foreground mt-4">
                  Don't have an account?{" "}
                  <a href="/signup" className="text-primary hover:underline">Sign up</a>
                </p>
              </form>
            </motion.div>
          </div>
        </main>
      </PageTransition>
      
      <Footer />
    </div>
  );
};

export default Login;
